import { apiClient } from "./client";
import type {
    ApiResponse,
    PageResponse,
    UserType,
    Gender,
    SubscriptionType,
    SubscriptionStatus,
} from "@/types/api";

// ============================================================================
// Types
// ============================================================================

export interface AdminUser {
    id: number;
    email: string;
    userType: UserType;
    fullName: string;
    displayName: string | null;
    phoneNumber: string | null;
    profileImageUrl: string | null;
    dateOfBirth: string | null;
    gender: Gender | null;
    country: string | null;
    city: string | null;
    isActive: boolean;
    isEmailVerified: boolean;
    termsAgreedAt: string | null;
    marketingAgreed: boolean;
    lastLoginAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreateUserRequest {
    email: string;
    password?: string;
    userType: UserType;
    fullName: string;
    displayName?: string;
    phoneNumber?: string;
    dateOfBirth?: string;
    gender?: Gender;
    isActive?: boolean;
    isEmailVerified?: boolean;
}

export interface UpdateUserRequest {
    email?: string;
    userType?: UserType;
    fullName?: string;
    displayName?: string;
    phoneNumber?: string;
    profileImageUrl?: string;
    dateOfBirth?: string;
    gender?: Gender;
    isActive?: boolean;
    isEmailVerified?: boolean;
}

export interface AdminSubscription {
    id: number;
    orderId: number;
    productId: number;
    pricingId: number;
    ownerUserId: number;
    ownerEmail: string;
    ownerFullName: string;
    organizationId: number | null;
    subscriptionType: SubscriptionType;
    status: SubscriptionStatus;
    maxSeats: number;
    usedSeats: number;
    validFrom: string;
    validUntil: string | null;
    autoRenew: boolean;
    nextBillingDate: string | null;
    cancelledAt: string | null;
    cancelledByUserId: number | null;
    cancellationReason: string | null;
    activatedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreateSubscriptionRequest {
    orderId: number;
    productId: number;
    pricingId: number;
    ownerUserId: number;
    organizationId?: number;
    subscriptionType: SubscriptionType;
    status?: SubscriptionStatus;
    maxSeats?: number;
    validFrom: string;
    validUntil?: string;
    autoRenew?: boolean;
    nextBillingDate?: string;
}

export interface UpdateSubscriptionRequest {
    status?: SubscriptionStatus;
    maxSeats?: number;
    validUntil?: string;
    autoRenew?: boolean;
    nextBillingDate?: string;
    cancellationReason?: string;
}

export type HierarchyLevel =
    | "program"
    | "curriculum"
    | "course"
    | "module"
    | "lesson";
export type ContentStatus = "draft" | "ready" | "published" | "archived";

// Product Types
export type ProductStatus = "draft" | "active" | "archived" | "discontinued";
export type PricingType = "one_time" | "subscription";
export type BillingPeriod = "monthly" | "quarterly" | "yearly" | "lifetime";

// Target Audience Categories
export const TARGET_AUDIENCE_OPTIONS = [
    { value: "individual", label: "일반 개인" },
    { value: "parent", label: "학부모" },
    { value: "student", label: "학생" },
    { value: "business", label: "기업/비즈니스" },
    { value: "academy", label: "학원" },
    { value: "school", label: "학교" },
    { value: "all", label: "전체" },
] as const;

export interface AdminProduct {
    id: number;
    name: string;
    slug: string | null;
    shortDescription: string | null;
    description: string | null;
    thumbnailUrl: string | null;
    bannerUrl: string | null;
    previewVideoUrl: string | null;
    features: string[] | null;
    targetAudience: string | null;
    learningOutcomes: string | null;
    status: ProductStatus;
    isActive: boolean;
    isFeatured: boolean;
    hasStockLimit: boolean;
    stockQuantity: number;
    displayOrder: number;
    tags: string | null;
    category: string | null;
    curriculumContentId?: number | null;
    publishedAt: string | null;
    createdAt: string;
    updatedAt: string;
    // 관련 데이터
    contents?: ProductContentItem[];
    pricings?: ProductPricing[];
}

export interface ProductContentItem {
    productId: number;
    contentId: number;
    sequenceOrder: number;
    isRequired: boolean;
    customTitle: string | null;
    customDescription: string | null;
    // 콘텐츠 정보
    contentTitle?: string;
    contentHierarchyLevel?: HierarchyLevel;
}

export interface ProductPricing {
    id: number;
    productId: number;
    name: string;
    pricingType: PricingType;
    billingPeriod: BillingPeriod;
    billingPeriodMonths: number | null;
    price: number;
    currency: string;
    originalPrice: number | null;
    discountPercentage: number | null;
    maxSeats: number;
    hasTrial: boolean;
    trialDays: number;
    isActive: boolean;
    isDefault: boolean;
    displayOrder: number;
    validFrom: string | null;
    validUntil: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreateProductRequest {
    name: string;
    slug?: string;
    shortDescription?: string;
    description?: string;
    thumbnailUrl?: string;
    bannerUrl?: string;
    previewVideoUrl?: string;
    features?: string[];
    targetAudience?: string;
    learningOutcomes?: string;
    status?: ProductStatus;
    isActive?: boolean;
    isFeatured?: boolean;
    hasStockLimit?: boolean;
    stockQuantity?: number;
    displayOrder?: number;
    tags?: string;
    category?: string;
    curriculumContentId?: number;
}

export interface UpdateProductRequest extends Partial<CreateProductRequest> {}

export interface CreateProductPricingRequest {
    productId: number;
    name: string;
    pricingType: PricingType;
    billingPeriod?: BillingPeriod;
    billingPeriodMonths?: number;
    price: number;
    currency?: string;
    originalPrice?: number;
    discountPercentage?: number;
    maxSeats?: number;
    hasTrial?: boolean;
    trialDays?: number;
    isActive?: boolean;
    isDefault?: boolean;
    displayOrder?: number;
    validFrom?: string;
    validUntil?: string;
}

export interface UpdateProductPricingRequest
    extends Partial<Omit<CreateProductPricingRequest, "productId">> {}

export interface AddProductContentRequest {
    productId: number;
    contentId: number;
    sequenceOrder?: number;
    isRequired?: boolean;
    customTitle?: string;
    customDescription?: string;
}

export interface AdminContent {
    id: number;
    notionPageId: string | null;
    hierarchyLevel: HierarchyLevel;
    status: ContentStatus;
    title: string;
    bareTitle: string | null;
    description: string | null;
    shortDescription: string | null;
    starRating: number | null;
    learningObjectives: string | null;
    concepts: string | null;
    estimatedDurationMinutes: number | null;
    thumbnailUrl: string | null;
    isActive: boolean;
    parentId: number | null;
    qid: string | null;
    overallSequenceOrder: number;
    createdAt: string;
    updatedAt: string;
}

export interface DashboardStats {
    totalUsers: number;
    activeUsers: number;
    totalSubscriptions: number;
    activeSubscriptions: number;
    totalContents: number;
    publishedContents: number;
    usersByType: Record<string, number>;
    subscriptionsByStatus: Record<string, number>;
}

// ============================================================================
// API Functions
// ============================================================================

export const adminApi = {
    // Dashboard
    getDashboardStats: async (): Promise<DashboardStats> => {
        const response = await apiClient.get<ApiResponse<DashboardStats>>(
            "/admin/dashboard/stats"
        );
        if (!response.data.data) {
            throw new Error("Failed to fetch dashboard stats");
        }
        return response.data.data;
    },

    // Users
    getUsers: async (
        params: {
            keyword?: string;
            userType?: UserType;
            isActive?: boolean;
            page?: number;
            size?: number;
        } = {}
    ): Promise<PageResponse<AdminUser>> => {
        const response = await apiClient.get<
            ApiResponse<PageResponse<AdminUser>>
        >("/admin/users", { params });
        if (!response.data.data) {
            throw new Error("Failed to fetch users");
        }
        return response.data.data;
    },

    getUserById: async (id: number): Promise<AdminUser> => {
        const response = await apiClient.get<ApiResponse<AdminUser>>(
            `/admin/users/${id}`
        );
        if (!response.data.data) {
            throw new Error("User not found");
        }
        return response.data.data;
    },

    createUser: async (data: CreateUserRequest): Promise<AdminUser> => {
        const response = await apiClient.post<ApiResponse<AdminUser>>(
            "/admin/users",
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to create user");
        }
        return response.data.data;
    },

    updateUser: async (
        id: number,
        data: UpdateUserRequest
    ): Promise<AdminUser> => {
        const response = await apiClient.put<ApiResponse<AdminUser>>(
            `/admin/users/${id}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update user");
        }
        return response.data.data;
    },

    toggleUserStatus: async (id: number): Promise<AdminUser> => {
        const response = await apiClient.patch<ApiResponse<AdminUser>>(
            `/admin/users/${id}/toggle-status`
        );
        if (!response.data.data) {
            throw new Error("Failed to toggle user status");
        }
        return response.data.data;
    },

    deleteUser: async (id: number): Promise<void> => {
        await apiClient.delete(`/admin/users/${id}`);
    },

    // Subscriptions
    getSubscriptions: async (
        params: {
            ownerId?: number;
            productId?: number;
            status?: SubscriptionStatus;
            subscriptionType?: SubscriptionType;
            page?: number;
            size?: number;
        } = {}
    ): Promise<PageResponse<AdminSubscription>> => {
        const response = await apiClient.get<
            ApiResponse<PageResponse<AdminSubscription>>
        >("/admin/subscriptions", { params });
        if (!response.data.data) {
            throw new Error("Failed to fetch subscriptions");
        }
        return response.data.data;
    },

    getSubscriptionById: async (id: number): Promise<AdminSubscription> => {
        const response = await apiClient.get<ApiResponse<AdminSubscription>>(
            `/admin/subscriptions/${id}`
        );
        if (!response.data.data) {
            throw new Error("Subscription not found");
        }
        return response.data.data;
    },

    createSubscription: async (
        data: CreateSubscriptionRequest
    ): Promise<AdminSubscription> => {
        const response = await apiClient.post<ApiResponse<AdminSubscription>>(
            "/admin/subscriptions",
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to create subscription");
        }
        return response.data.data;
    },

    updateSubscription: async (
        id: number,
        data: UpdateSubscriptionRequest
    ): Promise<AdminSubscription> => {
        const response = await apiClient.put<ApiResponse<AdminSubscription>>(
            `/admin/subscriptions/${id}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update subscription");
        }
        return response.data.data;
    },

    cancelSubscription: async (
        id: number,
        reason?: string
    ): Promise<AdminSubscription> => {
        const response = await apiClient.patch<ApiResponse<AdminSubscription>>(
            `/admin/subscriptions/${id}/cancel`,
            null,
            { params: { reason } }
        );
        if (!response.data.data) {
            throw new Error("Failed to cancel subscription");
        }
        return response.data.data;
    },

    deleteSubscription: async (id: number): Promise<void> => {
        await apiClient.delete(`/admin/subscriptions/${id}`);
    },

    // Contents
    getContents: async (
        params: {
            keyword?: string;
            hierarchyLevel?: HierarchyLevel;
            status?: ContentStatus;
            page?: number;
            size?: number;
        } = {}
    ): Promise<PageResponse<AdminContent>> => {
        const response = await apiClient.get<
            ApiResponse<PageResponse<AdminContent>>
        >("/admin/contents", { params });
        if (!response.data.data) {
            throw new Error("Failed to fetch contents");
        }
        return response.data.data;
    },

    getContentById: async (id: number): Promise<AdminContent> => {
        const response = await apiClient.get<ApiResponse<AdminContent>>(
            `/admin/contents/${id}`
        );
        if (!response.data.data) {
            throw new Error("Content not found");
        }
        return response.data.data;
    },

    getContentsByLevel: async (
        level: HierarchyLevel,
        includeInactive: boolean = false
    ): Promise<AdminContent[]> => {
        const response = await apiClient.get<ApiResponse<AdminContent[]>>(
            `/admin/contents/level/${level}`,
            {
                params: { includeInactive },
            }
        );
        return response.data.data || [];
    },

    getChildContents: async (parentId: number): Promise<AdminContent[]> => {
        const response = await apiClient.get<ApiResponse<AdminContent[]>>(
            `/admin/contents/${parentId}/children`
        );
        return response.data.data || [];
    },

    // Products
    getProducts: async (
        params: {
            keyword?: string;
            status?: ProductStatus;
            targetAudience?: string;
            isActive?: boolean;
            isFeatured?: boolean;
            page?: number;
            size?: number;
        } = {}
    ): Promise<PageResponse<AdminProduct>> => {
        const response = await apiClient.get<
            ApiResponse<PageResponse<AdminProduct>>
        >("/admin/products", { params });
        if (!response.data.data) {
            throw new Error("Failed to fetch products");
        }
        return response.data.data;
    },

    getProductById: async (id: number): Promise<AdminProduct> => {
        const response = await apiClient.get<ApiResponse<AdminProduct>>(
            `/admin/products/${id}`
        );
        if (!response.data.data) {
            throw new Error("Product not found");
        }
        return response.data.data;
    },

    createProduct: async (
        data: CreateProductRequest
    ): Promise<AdminProduct> => {
        const response = await apiClient.post<ApiResponse<AdminProduct>>(
            "/admin/products",
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to create product");
        }
        return response.data.data;
    },

    updateProduct: async (
        id: number,
        data: UpdateProductRequest
    ): Promise<AdminProduct> => {
        const response = await apiClient.put<ApiResponse<AdminProduct>>(
            `/admin/products/${id}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update product");
        }
        return response.data.data;
    },

    toggleProductStatus: async (id: number): Promise<AdminProduct> => {
        const response = await apiClient.patch<ApiResponse<AdminProduct>>(
            `/admin/products/${id}/toggle-status`
        );
        if (!response.data.data) {
            throw new Error("Failed to toggle product status");
        }
        return response.data.data;
    },

    deleteProduct: async (id: number): Promise<void> => {
        await apiClient.delete(`/admin/products/${id}`);
    },

    // Product Pricing
    getProductPricings: async (
        productId: number
    ): Promise<ProductPricing[]> => {
        const response = await apiClient.get<ApiResponse<ProductPricing[]>>(
            `/admin/products/${productId}/pricings`
        );
        return response.data.data || [];
    },

    createProductPricing: async (
        data: CreateProductPricingRequest
    ): Promise<ProductPricing> => {
        const response = await apiClient.post<ApiResponse<ProductPricing>>(
            `/admin/products/${data.productId}/pricings`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to create pricing");
        }
        return response.data.data;
    },

    updateProductPricing: async (
        productId: number,
        pricingId: number,
        data: UpdateProductPricingRequest
    ): Promise<ProductPricing> => {
        const response = await apiClient.put<ApiResponse<ProductPricing>>(
            `/admin/products/${productId}/pricings/${pricingId}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update pricing");
        }
        return response.data.data;
    },

    deleteProductPricing: async (
        productId: number,
        pricingId: number
    ): Promise<void> => {
        await apiClient.delete(
            `/admin/products/${productId}/pricings/${pricingId}`
        );
    },

    // Product Contents
    getProductContents: async (
        productId: number
    ): Promise<ProductContentItem[]> => {
        const response = await apiClient.get<ApiResponse<ProductContentItem[]>>(
            `/admin/products/${productId}/contents`
        );
        return response.data.data || [];
    },

    addProductContent: async (
        data: AddProductContentRequest
    ): Promise<ProductContentItem> => {
        const response = await apiClient.post<ApiResponse<ProductContentItem>>(
            `/admin/products/${data.productId}/contents`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to add content to product");
        }
        return response.data.data;
    },

    updateProductContent: async (
        productId: number,
        contentId: number,
        data: Partial<AddProductContentRequest>
    ): Promise<ProductContentItem> => {
        const response = await apiClient.put<ApiResponse<ProductContentItem>>(
            `/admin/products/${productId}/contents/${contentId}`,
            data
        );
        if (!response.data.data) {
            throw new Error("Failed to update product content");
        }
        return response.data.data;
    },

    removeProductContent: async (
        productId: number,
        contentId: number
    ): Promise<void> => {
        await apiClient.delete(
            `/admin/products/${productId}/contents/${contentId}`
        );
    },
};
